import React, { createContext, useContext, useState, useEffect } from 'react';
import jwtDecode from "jwt-decode";

const AuthContext = createContext({
  user: null,
  login: () => {},
  logout: () => {},
  isAuthenticated: false,
  isInitialized: false,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const checkTokenValidity = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      if (decoded.exp && decoded.exp < currentTime) {
        // Token has expired
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return null;
      }
      
      // Get the stored user data
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        return { ...decoded, ...JSON.parse(storedUser) };
      }
      
      return decoded;
    } catch (error) {
      console.error('Error decoding token:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    }
  };

  // Initial token check
  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem('token');
      if (token) {
        const validUser = checkTokenValidity(token);
        if (validUser) {
          setUser(validUser);
        }
      }
      setIsInitialized(true);
    };

    initializeAuth();
  }, []);

  const login = (authData) => {
    if (authData.token) {
      const validUser = checkTokenValidity(authData.token);
      if (validUser) {
        localStorage.setItem('token', authData.token);
        localStorage.setItem('user', JSON.stringify(authData.user));
        setUser({ ...validUser, ...authData.user });
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Set up automatic token check
  useEffect(() => {
    if (!isInitialized) return;

    const checkToken = () => {
      const token = localStorage.getItem('token');
      if (token) {
        const validUser = checkTokenValidity(token);
        if (!validUser && user) {
          // Token has expired, log out user
          logout();
        }
      }
    };

    // Check token every minute
    const interval = setInterval(checkToken, 60000);

    return () => clearInterval(interval);
  }, [user, isInitialized]);

  if (!isInitialized) {
    // You could return a loading spinner here if needed
    return null;
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      isAuthenticated: !!user,
      isInitialized
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);